import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import { baseurImage } from "../../Baseurl";
import axiosInstance from "../../api/axiosInstance";
import { fetchModuleMappings, hasSubModulePermission, isAdmin } from "../../utils/permissions";
import BackButton from "../Theme/BackButton";

export default function Insurance() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [insurances, setInsurances] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [patientsById, setPatientsById] = useState({});
  const [showPaid, setShowPaid] = useState(true);
  const [showUnpaid, setShowUnpaid] = useState(true);
  const [claimsByStatus, setClaimsByStatus] = useState({ paid: [], unpaid: [] });
  const [filterLoading, setFilterLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company_name: '',
    address: '',
    phone_number: '',
    email: '',
    website: '',
    company_logo: null
  });
  const itemsPerPage = 10;

  const [permissions, setPermissions] = useState({
    claims: false,
    companies: false
  });

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setPermissions({ claims: true, companies: true });
        return;
      }

      try {
        const allModules = await fetchModuleMappings();
        const insuranceModule = allModules.find(m => m.module_name.toLowerCase() === 'insurance');

        if (insuranceModule && insuranceModule.sub_modules) {
          const claimsSub = insuranceModule.sub_modules.find(sm => sm.sub_module_name === 'claims');
          const companiesSub = insuranceModule.sub_modules.find(sm => sm.sub_module_name === 'companies');

          setPermissions({
            claims: claimsSub ? hasSubModulePermission(claimsSub.id) : false,
            companies: companiesSub ? hasSubModulePermission(companiesSub.id) : false
          });
        } else {
          console.warn('Insurance module or submodules not found');
          setPermissions({ claims: false, companies: false });
        }
      } catch (err) {
        console.error("Error checking permissions", err);
        setPermissions({ claims: false, companies: false });
      }
    };

    checkPermissions();
  }, []);

  // Set active tab based on permissions
  // Set active tab based on permissions and stored state
  useEffect(() => {
    // Only set the tab if it hasn't been set yet (null)
    if (activeTab === null) {
      const storedTab = location.state?.activeTab || sessionStorage.getItem("insuranceActiveTab");
      let tabToSet = null;

      if (storedTab) {
        if (storedTab === "claims" && permissions.claims) tabToSet = "claims";
        else if (storedTab === "companies" && permissions.companies) tabToSet = "companies";
      }

      if (!tabToSet) {
        if (permissions.claims) {
          tabToSet = "claims";
        } else if (permissions.companies) {
          tabToSet = "companies";
        }
      }

      if (tabToSet) {
        setActiveTab(tabToSet);
      }
    }
  }, [permissions, activeTab, location.state]);

  // Persist active tab to sessionStorage
  useEffect(() => {
    if (activeTab) {
      sessionStorage.setItem("insuranceActiveTab", activeTab);
    }
  }, [activeTab]);

  // Payment flow state
  const [showPaymentStep1, setShowPaymentStep1] = useState(false);
  const [showPaymentStep2, setShowPaymentStep2] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [acceptedClaims, setAcceptedClaims] = useState([]);
  const [acceptedLoading, setAcceptedLoading] = useState(false);
  const [selectedClaimIds, setSelectedClaimIds] = useState(new Set());
  const [selectedTotal, setSelectedTotal] = useState(0);
  const [manualAmounts, setManualAmounts] = useState({}); // { claimId: amount }
  const [paymentForm, setPaymentForm] = useState({
    company_id: "",
    payment_date: "",
    total_amount: "",
    payment_ref: "",
    attachment: null
  });

  // Single claim payment state
  const [showSinglePaymentModal, setShowSinglePaymentModal] = useState(false);
  const [singlePaymentForm, setSinglePaymentForm] = useState({
    claim_id: "",
    company_id: "",
    payment_date: "",
    total_amount: "",
    payment_ref: "",
    attachment: null
  });
  const [singlePaymentLoading, setSinglePaymentLoading] = useState(false);

  const [exportLoading, setExportLoading] = useState(false);
  const [claimCounts, setClaimCounts] = useState({}); // { [companyId]: number }
  const [claimCountLoading, setClaimCountLoading] = useState(false);
  const [pendingAmounts, setPendingAmounts] = useState({});
  const [pendingAmountLoading, setPendingAmountLoading] = useState(false);

  const fetchClaimCountForCompany = async (companyId) => {
    try {
      const resp = await axiosInstance.get(`getClaimsByCompanyId/${companyId}`);
      const data = resp.data;
      const count = typeof data?.total_claims === 'number'
        ? data.total_claims
        : (Array.isArray(data?.data) ? data.data.length : (Array.isArray(data) ? data.length : 0));
      setClaimCounts(prev => ({ ...prev, [companyId]: count }));
    } catch (e) {
      console.error('Claim count error company', companyId, e);
      setClaimCounts(prev => ({ ...prev, [companyId]: 0 }));
    }
  };
  useEffect(() => {
    const run = async () => {
      if (!companies || companies.length === 0) return;
      setClaimCountLoading(true);
      setPendingAmountLoading(true);
      try {
        // Fetch claim counts and pending amounts in parallel per company
        await Promise.all([
          Promise.all(companies.map(c => fetchClaimCountForCompany(c.id))),
          Promise.all(companies.map(c => fetchPendingAmountForCompany(c.id)))
        ]);
      } finally {
        setClaimCountLoading(false);
        setPendingAmountLoading(false);
      }
    };
    run();
  }, [companies]);

  // Fetch pending amount for a company using getUnpaidClaimsByCompany/:id
  const fetchPendingAmountForCompany = async (companyId) => {
    try {
      const resp = await axiosInstance.get(`getUnpaidClaimsByCompany/${companyId}`);
      const data = resp.data;
      // API shape example: { success, data: { pendingAmount, results: [...] } }
      const amount = typeof data?.data?.pendingAmount === 'number'
        ? data.data.pendingAmount
        : (typeof data?.pendingAmount === 'number' ? data.pendingAmount : 0);
      setPendingAmounts(prev => ({ ...prev, [companyId]: amount }));
    } catch (e) {
      console.error('Pending amount error company', companyId, e);
      setPendingAmounts(prev => ({ ...prev, [companyId]: 0 }));
    }
  };

  // Update claim status (pending/accepted/rejected) similar to CompanyClaims.jsx
  const updateClaimStatus = async (claimId, newStatus) => {
    try {
      const resp = await axiosInstance.put(`updateInsuranceClaimStatus/${claimId}`, { status: newStatus });
      const data = resp.data || {};
      if (data && (data.success || data.ok)) {
        // Update in main list
        setInsurances(prev => prev.map(c => String(c.id) === String(claimId) ? { ...c, status: (newStatus || '').toLowerCase() } : c));
        // Update filtered buckets if present
        setClaimsByStatus(prev => ({
          paid: (prev.paid || []).map(c => String(c.id) === String(claimId) ? { ...c, status: (newStatus || '').toLowerCase() } : c),
          unpaid: (prev.unpaid || []).map(c => String(c.id) === String(claimId) ? { ...c, status: (newStatus || '').toLowerCase() } : c)
        }));
        Swal.fire('Success', 'Status updated successfully', 'success');
      } else {
        Swal.fire('Error', data.message || 'Failed to update status', 'error');
      }
    } catch (e) {
      console.error('Update claim status error:', e);
      Swal.fire('Error', 'Server error while updating status', 'error');
    }
  };

  const openPayment = async () => {
    setPaymentForm({ company_id: "", payment_date: "", total_amount: "", payment_ref: "", attachment: null });
    setSelectedClaimIds(new Set());
    setSelectedTotal(0);
    setManualAmounts({});
    setAcceptedClaims([]);
    setShowPaymentStep2(false);
    setShowPaymentStep1(true);
  };

  const handlePaymentInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'attachment') {
      setPaymentForm(prev => ({ ...prev, attachment: files && files[0] ? files[0] : null }));
    } else {
      setPaymentForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // No active-companies filter here per request

  const loadAcceptedClaimsByCompany = async (companyId) => {
    try {
      setAcceptedLoading(true);

      let rows = [];


      const candidateEndpoints = [
        `getUnpaidClaimsByCompanyId/${companyId}`,
        `getPayableClaimsByCompanyId/${companyId}`,
        `getClaimsByCompanyId/${companyId}?payment_status=unpaid`,
        `getClaimsByCompanyId/${companyId}?status=accepted&payment_status=pending`,
        `getAcceptedUnpaidClaimsByCompanyId/${companyId}`
      ];

      for (const endpoint of candidateEndpoints) {
        try {
          const resp = await axiosInstance.get(endpoint);
          const data = resp.data || null;
          if (data && (Array.isArray(data?.data) || Array.isArray(data))) {
            rows = Array.isArray(data.data) ? data.data : data;
            if (rows && rows.length > 0) {
              console.log('Using claims from:', endpoint, 'count:', rows.length);
              break;
            }
          }
        } catch (e) {
          console.warn('Failed candidate URL', endpoint, e);
        }
      }

      // Normalize rows in case API returns different field names
      if (rows && rows.length > 0) {
        rows = rows.map((c) => ({
          claim_id: c.claim_id ?? c.id ?? c.claimId,
          claim_number: c.claim_number ?? c.claim_no ?? c.claimNo ?? c.id,
          created_at: c.created_at ?? c.date ?? c.createdAt,
          patient_name: c.patient_name ?? c.patient ?? c.patientName,
          policy_number: c.policy_number ?? c.policy_id ?? c.policyId,
          insurance_amount: Number(c.insurance_amount ?? c.amount ?? 0)
        }));
      }

      // Fallback: filter already-fetched list to only unpaid/partial items
      if (!rows || rows.length === 0) {
        console.log('Using fallback - filtering from insurances list for unpaid/partial');
        rows = (insurances || [])
          .filter(c => {
            const status = (c.status || '').toLowerCase();
            const companyMatch = c.company_id === companyId || c.company === (companies.find(x => x.id === companyId)?.companyName);
            // unpaid or partial-paid: exclude fully paid
            const unpaidOrPartial = !(status === 'paid' || status === 'fully paid');
            return companyMatch && unpaidOrPartial;
          })
          .map(c => ({
            claim_id: c.id,
            claim_number: c.claim_no,
            created_at: c.date,
            patient_name: c.patient_name || patientsById[c.patient_id] || 'Unknown Patient',
            policy_number: c.policy_id,
            insurance_amount: c.amount
          }));
      }

      console.log('Final payable claims:', rows);
      setAcceptedClaims(rows || []);
    } catch (error) {
      console.error('Error loading payable claims:', error);
      setAcceptedClaims([]);
    } finally {
      setAcceptedLoading(false);
    }
  };

  const proceedToSelectClaims = async (e) => {
    e.preventDefault();
    if (!paymentForm.company_id) {
      Swal.fire('Error', 'Please select a company', 'error');
      return;
    }
    setManualAmounts({}); // Reset manual amounts when proceeding to step 2
    await loadAcceptedClaimsByCompany(Number(paymentForm.company_id));
    setShowPaymentStep1(false);
    setShowPaymentStep2(true);
  };

  const toggleSelectClaim = (claim) => {
    const newSet = new Set(selectedClaimIds);
    const originalAmount = Number(claim.insurance_amount || claim.amount || 0);
    const manualAmount = manualAmounts[claim.claim_id];
    const claimAmount = manualAmount !== undefined ? manualAmount : originalAmount;

    console.log('Toggling claim:', claim, 'Original Amount:', originalAmount, 'Manual Amount:', manualAmount, 'Final Amount:', claimAmount);

    if (newSet.has(claim.claim_id)) {
      newSet.delete(claim.claim_id);
      setSelectedTotal(prev => Number(prev) - claimAmount);
    } else {
      newSet.add(claim.claim_id);
      setSelectedTotal(prev => Number(prev) + claimAmount);
    }
    setSelectedClaimIds(newSet);
  };

  const handleManualAmountChange = (claimId, value) => {
    const amount = Number(value) || 0;
    setManualAmounts(prev => ({
      ...prev,
      [claimId]: amount
    }));

    // Recalculate total if claim is selected
    if (selectedClaimIds.has(claimId)) {
      const claim = acceptedClaims.find(c => c.claim_id === claimId);
      const originalAmount = Number(claim?.insurance_amount || claim?.amount || 0);
      const previousManualAmount = manualAmounts[claimId] !== undefined ? manualAmounts[claimId] : originalAmount;
      setSelectedTotal(prev => Number(prev) - previousManualAmount + amount);
    }
  };

  const submitPayment = async () => {
    if (selectedClaimIds.size === 0) {
      Swal.fire('Select Claims', 'Please select at least one claim to pay', 'warning');
      return;
    }
    try {
      setPaymentLoading(true);
      // Build claims payload with entered_amount from accepted claims (fallback to main list)
      const selectedClaimsArr = Array.from(selectedClaimIds);
      const claimsPayload = selectedClaimsArr.map((id) => {
        const fromAccepted = (acceptedClaims || []).find((c) => String(c.claim_id) === String(id));
        const fromAll = (insurances || []).find((c) => String(c.id) === String(id));

        // Use manual amount if available, otherwise use original amount
        const manualAmount = manualAmounts[id];
        const originalAmount = Number(
          (fromAccepted && (fromAccepted.insurance_amount || fromAccepted.amount)) ??
          (fromAll && fromAll.amount) ??
          0
        );
        const finalAmount = manualAmount !== undefined ? manualAmount : originalAmount;

        console.log('Claim ID:', id, 'Original Amount:', originalAmount, 'Manual Amount:', manualAmount, 'Final Amount:', finalAmount);
        return { claim_id: Number(id), entered_amount: finalAmount };
      });

      // Build form-data per API contract
      const formData = new FormData();
      formData.append('company_id', String(paymentForm.company_id));
      formData.append('payment_date', paymentForm.payment_date || new Date().toISOString().slice(0, 10));
      formData.append('payment_reference', paymentForm.payment_ref || '');
      formData.append('total_amount', String(Number(selectedTotal || 0)));
      if (paymentForm.attachment) formData.append('document', paymentForm.attachment);
      formData.append('claims', JSON.stringify(claimsPayload));

      // Call processInsurancePayment endpoint
      const resp = await axiosInstance.post(`processInsurancePayment`, formData);
      const data = resp.data || {};
      const ok = !!(data && (data.success || data.ok));

      if (!ok) {
        throw new Error(data?.message || 'Failed to process insurance payment');
      }

      Swal.fire('Success', 'Payment recorded successfully', 'success');
      setShowPaymentStep1(false);
      setShowPaymentStep2(false);
      setSelectedClaimIds(new Set());
      setSelectedTotal(0);
      setManualAmounts({});
      await fetchAllInsuranceClaims();
    } catch (error) {
      console.error('Payment error:', error);
      Swal.fire('Error', 'Failed to submit payment', 'error');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Single claim payment functions
  const openSinglePayment = (claim) => {
    // Find company_id if not present in claim data
    let companyId = claim.company_id;
    if (!companyId && claim.company) {
      const foundCompany = companies.find(c => c.companyName === claim.company);
      companyId = foundCompany?.id || "";
    }

    console.log('Opening payment for claim:', claim);
    console.log('Company ID found:', companyId);

    setSinglePaymentForm({
      claim_id: claim.id,
      company_id: companyId,
      payment_date: "",
      total_amount: claim.amount || "",
      payment_ref: "",
      attachment: null
    });
    setShowSinglePaymentModal(true);
  };

  const handleSinglePaymentInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'attachment') {
      setSinglePaymentForm(prev => ({ ...prev, attachment: files && files[0] ? files[0] : null }));
    } else {
      setSinglePaymentForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const submitSinglePayment = async () => {
    try {
      setSinglePaymentLoading(true);

      // Validate required fields
      if (!singlePaymentForm.company_id) {
        Swal.fire('Error', 'Company ID is missing. Please try again.', 'error');
        return;
      }

      if (!singlePaymentForm.claim_id) {
        Swal.fire('Error', 'Claim ID is missing. Please try again.', 'error');
        return;
      }

      console.log('Submitting payment with:', {
        company_id: singlePaymentForm.company_id,
        claim_id: singlePaymentForm.claim_id,
        total_amount: singlePaymentForm.total_amount
      });

      // Build claims payload for single claim
      const claimsPayload = [{
        claim_id: Number(singlePaymentForm.claim_id),
        entered_amount: Number(singlePaymentForm.total_amount || 0)
      }];

      // Build form-data per API contract
      const formData = new FormData();
      formData.append('company_id', String(singlePaymentForm.company_id));
      formData.append('payment_date', singlePaymentForm.payment_date || new Date().toISOString().slice(0, 10));
      formData.append('payment_reference', singlePaymentForm.payment_ref || '');
      formData.append('total_amount', String(Number(singlePaymentForm.total_amount || 0)));
      if (singlePaymentForm.attachment) formData.append('document', singlePaymentForm.attachment);
      formData.append('claims', JSON.stringify(claimsPayload));

      // Call processInsurancePayment endpoint
      const resp = await axiosInstance.post(`processInsurancePayment`, formData);
      const data = resp.data || {};
      const ok = !!(data && (data.success || data.ok));

      if (!ok) {
        throw new Error(data?.message || 'Failed to process insurance payment');
      }

      Swal.fire('Success', 'Payment recorded successfully', 'success');
      setShowSinglePaymentModal(false);
      setSinglePaymentForm({
        claim_id: "",
        company_id: "",
        payment_date: "",
        total_amount: "",
        payment_ref: "",
        attachment: null
      });
      await fetchAllInsuranceClaims();
    } catch (error) {
      console.error('Single payment error:', error);
      Swal.fire('Error', 'Failed to submit payment', 'error');
    } finally {
      setSinglePaymentLoading(false);
    }
  };

  // Fetch claims by status from API
  const fetchClaimsByStatus = async (status) => {
    try {
      console.log(`Fetching ${status} claims from API...`);
      const response = await axiosInstance.post(`getInsuranceClaimsByStatus`, { filterType: status });


      const data = response.data;

      if (data.success) {
        const rows = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
        const transformed = rows.map((c) => ({
          id: c.id || c.claim_id,
          claim_no: c.claim_number,
          date: c.created_at,
          company: c.company_name,
          company_id: c.company_id,
          patient_id: c.patient_id,
          patient_name: c.patient_name,
          policy_id: c.policy_number,
          amount: Number(c.insurance_amount || c.amount || 0),
          status: (c.status || '').toLowerCase(),
          payment_status: (c.payment_status || '').toLowerCase()
        }));
        console.log(`Transformed ${status} claims:`, transformed);
        return transformed;
      } else {
        console.warn(`API returned success: false for ${status} claims:`, data);
        return [];
      }
    } catch (error) {
      console.error(`Error fetching ${status} claims:`, error);
      return [];
    }
  };

  // Fetch all insurance claims from backend using the original "get all" API
  const fetchAllInsuranceClaims = async () => {
    try {
      setLoading(true);
      console.log('Fetching all claims using get all API...');

      // Use the original "get all" API endpoint
      const response = await axiosInstance.get(`getAllInsuranceClaims`);

      const data = response.data;

      if (data.success) {
        const rows = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
        const transformed = rows.map((c) => ({
          id: c.id || c.claim_id,
          claim_no: c.claim_number,
          date: c.created_at,
          company: c.company_name,
          company_id: c.company_id,
          patient_id: c.patient_id,
          patient_name: c.patient_name,
          policy_id: c.policy_number,
          amount: Number(c.insurance_amount || c.amount || 0),
          status: (c.status || '').toLowerCase(),
          payment_status: (c.payment_status || '').toLowerCase()
        }));

        // Separate paid and unpaid for filter counts
        const paidClaims = transformed.filter(c =>
          c.payment_status === 'fully_paid' || c.payment_status === 'paid'
        );
        const unpaidClaims = transformed.filter(c =>
          c.payment_status !== 'fully_paid' && c.payment_status !== 'paid'
        );

        setClaimsByStatus({ paid: paidClaims, unpaid: unpaidClaims });
        setInsurances(transformed);

        // Build a quick patient map for display
        const patientMap = {};
        transformed.forEach((r) => {
          if (r.patient_id) patientMap[r.patient_id] = r.patient_name || '';
        });
        setPatientsById(patientMap);
      } else {
        console.warn('Get all API returned success: false:', data);
        setClaimsByStatus({ paid: [], unpaid: [] });
        setInsurances([]);
      }

    } catch (error) {
      console.error("Error fetching all insurance claims:", error);
      setError(true);
      Swal.fire("Error", "Failed to fetch insurance claims", "error");
    } finally {
      setLoading(false);
    }
  };

  // API functions for insurance companies
  const fetchInsuranceCompanies = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`getAllInsuranceCompanies`);
      const data = response.data;
      if (data.success) {
        // Transform API data to match component structure
        const transformedCompanies = data.data.map(company => {
          const logoUrl = company.company_logo ? `${baseurImage}uploads/company_logos/${company.company_logo}` : null;
          return {
            id: company.company_id,
            logo: company.company_logo,
            companyName: company.company_name,
            telephone: company.phone_number,
            address: company.address,
            website: company.website,
            email: company.email,
            status: (company.status || company.company_status || 'active').toString().toLowerCase(),
            pendingClaims: 0, // These would need separate API calls
            pendingAmount: 0,
            paidClaims: 0,
            paidAmount: 0,
            isDeleted: company.isDeleted,
            created_at: company.created_at,
            updated_at: company.updated_at
          };
        });
        setCompanies(transformedCompanies);
      } else {
        setError(true);
        Swal.fire("Error", "Failed to fetch insurance companies", "error");
      }
    } catch (error) {
      console.error("Error fetching insurance companies:", error);
      setError(true);
      Swal.fire("Error", "Failed to fetch insurance companies", "error");
    } finally {
      setLoading(false);
    }
  };

  // Update company status (active/inactive)
  const updateInsuranceCompanyStatus = async (companyId, status) => {
    try {
      const response = await axiosInstance.put(`updateInsuranceCompanyStatus`, { company_id: Number(companyId), status });
      const data = response.data;
      if (data && (data.success || data.ok)) {
        // Optimistically update local state
        setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, status } : c));
        Swal.fire('Success', data.message || 'Status updated successfully.', 'success');
        return true;
      }
      throw new Error(data?.message || 'Failed to update status');
    } catch (e) {
      console.error('updateInsuranceCompanyStatus error:', e);
      Swal.fire('Error', e.message || 'Failed to update status', 'error');
      return false;
    }
  };

  const updateInsuranceCompany = async (companyId, updatedData) => {
    try {
      const response = await axiosInstance.put(`updateInsuranceCompany/${companyId}`, updatedData);
      const data = response.data;
      if (data.success) {
        await fetchInsuranceCompanies();
        Swal.fire("Success", "Insurance company updated successfully", "success");
        return true;
      } else {
        Swal.fire("Error", data.message || "Failed to update insurance company", "error");
        return false;
      }
    } catch (error) {
      console.error("Error updating insurance company:", error);
      Swal.fire("Error", "Failed to update insurance company", "error");
      return false;
    }
  };
  const deleteInsuranceCompany = async (companyId) => {
    try {
      const response = await axiosInstance.delete(`deleteInsuranceCompany/${companyId}`);
      const data = response.data;
      if (data.success) {
        await fetchInsuranceCompanies();
        Swal.fire("Success", "Insurance company deleted successfully", "success");
        return true;
      } else {
        Swal.fire("Error", data.message || "Failed to delete insurance company", "error");
        return false;
      }
    } catch (error) {
      console.error("Error deleting insurance company:", error);
      Swal.fire("Error", "Failed to delete insurance company", "error");
      return false;
    }
  };
  const createInsuranceCompany = async (companyData) => {
    try {
      const formData = new FormData();
      formData.append('company_name', companyData.company_name);
      formData.append('address', companyData.address);
      formData.append('phone_number', companyData.phone_number);
      formData.append('email', companyData.email);
      formData.append('website', companyData.website);
      if (companyData.company_logo) {
        formData.append('company_logo', companyData.company_logo);
      }
      const response = await axiosInstance.post(`createInsuranceCompany`, formData);
      const data = response.data;
      if (data.success) {
        await fetchInsuranceCompanies();
        Swal.fire("Success", "Insurance company created successfully", "success");
        return true;
      } else {
        Swal.fire("Error", data.message || "Failed to create insurance company", "error");
        return false;
      }
    } catch (error) {
      console.error("Error creating insurance company:", error);
      Swal.fire("Error", "Failed to create insurance company", "error");
      return false;
    }
  };
  const exportInsuranceClaimsReport = async () => {
    try {
      setExportLoading(true);

      // Determine payment status based on filter checkboxes
      let paymentStatus = '';
      if (showPaid && !showUnpaid) {
        paymentStatus = 'Paid';
      } else if (!showPaid && showUnpaid) {
        paymentStatus = 'Unpaid';
      } else {
        // Both selected or both unselected - export all
        paymentStatus = 'All';
      }

      const response = await axiosInstance.get(`dashboard/insurance_claims_report?paymentStatus=${paymentStatus}`, {
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
        },
        responseType: 'blob'
      });

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `insurance_claims_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        Swal.fire("Success", "Insurance claims report exported successfully", "success");
      } else {
        const text = await response.data.text();
        const data = JSON.parse(text);
        if (data.success) {
          const csvContent = convertToCSV(data.data || []);
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `insurance_claims_report_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          Swal.fire("Success", "Insurance claims report exported successfully", "success");
        } else {
          throw new Error(data.message || "Failed to export insurance claims report");
        }
      }
    } catch (error) {
      console.error("Error exporting insurance claims report:", error);
      Swal.fire("Error", "Failed to export insurance claims report", "error");
    } finally {
      setExportLoading(false);
    }
  };
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    const csvRows = data.map(row => {
      return headers.map(header => {
        const value = row[header];
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      }).join(',');
    });
    return [csvHeaders, ...csvRows].join('\n');
  };
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.company_name || !newCompany.address || !newCompany.phone_number || !newCompany.email) {
      Swal.fire("Error", "Please fill in all required fields", "error");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCompany.email)) {
      Swal.fire("Error", "Please enter a valid email address", "error");
      return;
    }

    const success = await createInsuranceCompany(newCompany);
    if (success) {
      setShowCreateModal(false);
      setNewCompany({
        company_name: '',
        address: '',
        phone_number: '',
        email: '',
        website: '',
        company_logo: null
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'company_logo') {
      setNewCompany(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setNewCompany(prev => ({ ...prev, [name]: value }));
    }
  };


  // const staticPatients = {
  //   887956: "Ali Saeed",
  //   563216598: "May Azam",
  //   56413521: "Jason Whitehouse",
  //   56416518: "Rajesh Kumar",
  //   123456789: "Sarah Ahmed",
  //   987654321: "Mohammed Ali",
  //   111222333: "Fatima Hassan",
  //   444555666: "Ahmed Al-Mansoori"
  // };

  useEffect(() => {
    // Load claims and companies from API
    fetchAllInsuranceClaims();
    fetchInsuranceCompanies();
  }, []);

  // Refresh claims when filter state changes
  useEffect(() => {
    if (activeTab === "claims") {
      refreshClaimsByFilter();
    }
  }, [showPaid, showUnpaid]);

  // const handleDelete = async (id) => {
  //   const confirm = await Swal.fire({
  //     title: "Delete this record?",
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonText: "Delete",
  //   });
  //   if (!confirm.isConfirmed) return;

  //   // Simulate API delay
  //   await new Promise(resolve => setTimeout(resolve, 500));

  //   setInsurances((prev) => prev.filter((i) => i.id !== id));
  //   Swal.fire("Deleted", "Insurance deleted successfully", "success");
  // };

  const handleDeleteCompany = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this company?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;

    // Call API to delete company
    await deleteInsuranceCompany(id);
  };

  const isNonPayable = (row) => {
    const status = (row.status || "").toLowerCase();
    const pstatus = (row.payment_status || "").toLowerCase();
    const isPaid = pstatus === 'fully_paid' || pstatus === 'paid' || status === 'paid' || status === 'approved';
    const isPending = status === 'pending' || pstatus === 'pending';
    return isPaid || isPending;
  };

  const payButtonLabel = (row) => {
    const status = (row.status || "").toLowerCase();
    const pstatus = (row.payment_status || "").toLowerCase();
    if (pstatus === 'fully_paid' || pstatus === 'paid' || status === 'paid' || status === 'approved') return 'Paid';
    if (status === 'pending' || pstatus === 'pending') return 'Pending';
    return 'Pay';
  };

  // Function to refresh claims when filter changes
  const refreshClaimsByFilter = async () => {
    try {
      setFilterLoading(true);

      // If both are selected OR both are unselected, use the original "get all" API
      if ((showPaid && showUnpaid) || (!showPaid && !showUnpaid)) {
        await fetchAllInsuranceClaims();
        return;
      }

      // If only one filter is selected, use the filter API
      let filterType = '';
      if (showPaid && !showUnpaid) {
        filterType = 'paid';
      } else if (!showPaid && showUnpaid) {
        filterType = 'unpaid';
      }


      // Fetch filtered claims
      const filteredClaims = await fetchClaimsByStatus(filterType);

      // Update state based on filter type
      if (filterType === 'paid') {
        setClaimsByStatus({ paid: filteredClaims, unpaid: [] });
        setInsurances(filteredClaims);
      } else if (filterType === 'unpaid') {
        setClaimsByStatus({ paid: [], unpaid: filteredClaims });
        setInsurances(filteredClaims);
      }

      // Build a quick patient map for display
      const patientMap = {};
      filteredClaims.forEach((r) => {
        if (r.patient_id) patientMap[r.patient_id] = r.patient_name || '';
      });
      setPatientsById(patientMap);

    } catch (error) {
      console.error("Error refreshing claims by filter:", error);
      setError(true);
    } finally {
      setFilterLoading(false);
    }
  };

  const filteredClaims = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = [];

    // Use appropriate data based on filter state
    if ((showPaid && showUnpaid) || (!showPaid && !showUnpaid)) {
      // Both selected or both unselected - show all claims
      rows = insurances || [];
    } else if (showPaid && !showUnpaid) {
      // Only paid selected - show paid claims
      rows = claimsByStatus.paid || [];
    } else if (!showPaid && showUnpaid) {
      // Only unpaid selected - show unpaid claims
      rows = claimsByStatus.unpaid || [];
    } else {
      // No filters selected (shouldn't happen with new logic)
      rows = [];
    }

    if (!q) return rows;
    return rows.filter((row) => {
      const patientName = patientsById[row.patient_id] || "";
      return (
        (row.company && row.company.toLowerCase().includes(q)) ||
        (row.services && row.services.toLowerCase().includes(q)) ||
        (row.claim_no && String(row.claim_no).toLowerCase().includes(q)) ||
        patientName.toLowerCase().includes(q)
      );
    });
  }, [search, insurances, patientsById, showPaid, showUnpaid, claimsByStatus]);

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = companies || [];

    if (!q) return rows;
    return rows.filter((row) => {
      return (
        (row.companyName && row.companyName.toLowerCase().includes(q)) ||
        (row.telephone && row.telephone.toLowerCase().includes(q)) ||
        (row.address && row.address.toLowerCase().includes(q)) ||
        (row.website && row.website.toLowerCase().includes(q)) ||
        (row.email && row.email.toLowerCase().includes(q))
      );
    });
  }, [search, companies]);

  const currentData = activeTab === "claims" ? filteredClaims : filteredCompanies;
  const totalPages = Math.ceil(currentData.length / itemsPerPage) || 1;
  const paginated = currentData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);






  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <button
              onClick={() => navigate(-1)}
              title="Back"
              style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', color: '#5B6B79', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, marginRight: 4, flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = '#4680ff22'; e.currentTarget.style.color = '#4680ff' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = '#5B6B79' }}
            ><i className="ti ti-arrow-left" /></button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#4680ff20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-shield-check" style={{ color: '#4680ff', fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Insurance</div>
              <div style={{ fontSize: 11, color: '#5B6B79' }}>Manage insurance claims and companies</div>
            </div>
          </div>
          <div className="searchParent">
            {(activeTab === null) ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <ul className="nav nav-tabs mb-3" style={{ borderBottom: "2px solid #dee2e6" }}>
                <li className="nav-item">
                  <button
                    className={`nav-link${activeTab === "claims" ? " active fw-semibold" : ""}`}
                    style={{
                      cursor: permissions.claims ? "pointer" : "not-allowed",
                      opacity: permissions.claims ? 1 : 0.5,
                      fontSize: 14,
                      padding: "8px 20px",
                      border: "none",
                      borderBottom: activeTab === "claims" ? "3px solid #4a90e2" : "3px solid transparent",
                      color: activeTab === "claims" ? "#4a90e2" : "#6c757d",
                      background: "transparent",
                    }}
                    onClick={() => {
                      if (!permissions.claims) return;
                      setActiveTab("claims");
                      setSearch("");
                      setCurrentPage(1);
                    }}
                  >
                    <i className="ti ti-file-text me-2"></i>Claims
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link${activeTab === "companies" ? " active fw-semibold" : ""}`}
                    style={{
                      cursor: permissions.companies ? "pointer" : "not-allowed",
                      opacity: permissions.companies ? 1 : 0.5,
                      fontSize: 14,
                      padding: "8px 20px",
                      border: "none",
                      borderBottom: activeTab === "companies" ? "3px solid #4a90e2" : "3px solid transparent",
                      color: activeTab === "companies" ? "#4a90e2" : "#6c757d",
                      background: "transparent",
                    }}
                    onClick={() => {
                      if (!permissions.companies) return;
                      setActiveTab("companies");
                      setSearch("");
                      setCurrentPage(1);
                    }}
                  >
                    <i className="ti ti-building me-2"></i>Companies
                  </button>
                </li>
              </ul>)}
          </div>

          {/* Show loading while permissions are being checked */}
          {activeTab === null ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="card table-card">
                <div className="card-header bg-white">
                  <div className="d-sm-flex align-items-center justify-content-between">
                    <div style={{ width: "300px", margin: "15px", position: "relative" }}>
                      <i
                        className="ti ti-search"
                        style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", zIndex: 2, color: "#6c757d", fontSize: "16px" }}
                      ></i>
                      <input
                        type="text"
                        className="form-control m-0"
                        placeholder={activeTab === "claims" ? "Search by company, patient, service" : "Search by company name, email, phone, address"}
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                        style={{ paddingLeft: "40px" }}
                      />
                    </div>
                    <div className="d-flex align-items-center">
                      {activeTab === "claims" && (
                        <div className="d-flex align-items-center gap-3 my-3 mx-2">
                          {filterLoading && (
                            <div className="d-flex align-items-center gap-2 text-muted">
                              <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <small>Updating filters...</small>
                            </div>
                          )}
                          {!filterLoading && (
                            <div className="text-muted small">
                              {((showPaid && showUnpaid) || (!showPaid && !showUnpaid)) ?
                                'Showing all claims' :
                                `Showing ${showPaid ? 'paid' : 'unpaid'} claims only`
                              }
                            </div>
                          )}
                          <label className="small d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={showPaid}
                              onChange={(e) => {
                                setShowPaid(e.target.checked);
                                setCurrentPage(1);
                              }}
                              style={{ cursor: 'pointer' }}
                              disabled={filterLoading}
                            />
                            <span>Paid ({claimsByStatus.paid?.length || 0})</span>
                          </label>
                          <label className="small d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={showUnpaid}
                              onChange={(e) => {
                                setShowUnpaid(e.target.checked);
                                setCurrentPage(1);
                              }}
                              style={{ cursor: 'pointer' }}
                              disabled={filterLoading}
                            />
                            <span>Unpaid ({claimsByStatus.unpaid?.length || 0})</span>
                          </label>
                        </div>
                      )}

                      <div className="d-flex gap-2" style={{ margin: "15px" }}>
                        {activeTab === "claims" && (
                          <>
                            <button
                              className="btn btn-primary btn-sm p-1"
                              onClick={exportInsuranceClaimsReport}
                              disabled={exportLoading}
                            >
                              {exportLoading ? 'Exporting...' : 'Export'}
                            </button>
                            {/* <button
                          className="btn btn-primary"
                          onClick={() => navigate("/Admin/AddInsurance")}
                        >
                          Add Insurance
                        </button> */}
                            <button
                              className="btn btn-primary btn-sm p-1"
                              onClick={openPayment}
                            >
                              Payment
                            </button>
                          </>
                        )}
                        {activeTab === "companies" && (
                          <button
                            className="btn btn-outline-primary btn-sm rounded"
                            onClick={() => setShowCreateModal(true)}
                          >
                            Add Company
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body p-0">
                  {loading && <div className="text-center my-4">Loading...</div>}
                  {error && !loading && <div className="text-center text-danger my-4">Failed to load data</div>}
                  {!loading && !error && (
                    <div className="table-responsive">
                      {activeTab === "claims" ? (
                        <table className="table m-0" id="pc-dt-simple">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Date</th>
                              <th>Company</th>
                              <th>Patient ID</th>
                              <th>Patient</th>
                              <th>Policy ID</th>
                              <th>Ins Amount</th>
                              <th>Status</th>
                              <th>Payment Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginated.length === 0 ? (
                              <tr>
                                <td colSpan="9" className="text-center text-muted">No data found</td>
                              </tr>
                            ) : (
                              paginated.map((row, index) => (
                                <tr key={row.id}>
                                  <td>{row.claim_no || `C${String(row.id).padStart(5, '0')}`}</td>
                                  <td>{row.date ? new Date(row.date).toLocaleDateString("en-GB") : "-"}</td>
                                  <td>{row.company || "-"}</td>
                                  <td>{row.patient_id || "-"}</td>
                                  <td>
                                    <NavLink
                                      to="/Admin/Manageappointment"
                                      state={{ patientid: { id: row.patient_id } }}
                                      style={{
                                        textDecoration: "none",
                                        color: "black",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {patientsById[row.patient_id] || row.patient_name || "-"}
                                    </NavLink>
                                  </td>

                                  <td>{row.policy_id || "-"}</td>
                                  <td>{row.amount != null ? `${row.amount} BHD` : "-"}</td>
                                  <td>
                                    <select
                                      id={`status-select-${row.id}`}
                                      className="form-select form-select-sm border-primary shadow-sm px-1 py-0"
                                      style={{ minWidth: 90, fontWeight: 500, fontSize: '0.85em', height: '1.8em', backgroundColor: '#f8f9fa' }}
                                      value={(row.status || 'pending').toLowerCase()}
                                      title="Change status"
                                      disabled={(row.status || '').toLowerCase() === 'accepted'}
                                      onChange={async (e) => {
                                        const newStatus = e.target.value;
                                        await updateClaimStatus(row.id, newStatus);
                                      }}
                                    >
                                      <option value="pending" disabled={(row.status || '').toLowerCase() === 'pending'}>Pending</option>
                                      <option value="accepted" disabled={(row.status || '').toLowerCase() === 'accepted'}>Accepted</option>
                                      <option value="rejected" disabled={(row.status || '').toLowerCase() === 'rejected'}>Rejected</option>
                                    </select>
                                  </td>
                                  <td>
                                    <span className={`badge ${row.payment_status === 'fully_paid' || row.payment_status === 'paid'
                                      ? 'bg-success'
                                      : row.payment_status === 'partial' || row.payment_status === 'partially_paid'
                                        ? 'bg-warning'
                                        : 'bg-danger'
                                      }`}>
                                      {row.payment_status === 'fully_paid' || row.payment_status === 'paid'
                                        ? 'Paid'
                                        : row.payment_status === 'partial' || row.payment_status === 'partially_paid'
                                          ? 'Partial'
                                          : 'Unpaid'}
                                    </span>
                                  </td>
                                  <td className="d-flex align-items-center gap-2">
                                    <div
                                      onClick={() => navigate(`/Admin/ViewInsurance/${row.id}`, { state: { insurance: row } })}
                                      style={{ cursor: "pointer" }}
                                      className="avtar avtar-xs btn-link-secondary viewIcon"
                                    >
                                      <i className="ti ti-eye f-20" />
                                    </div>
                                    {/* <div
                                  onClick={() => navigate(`/Admin/EditInsurance`, { state: { insurance: row } })}
                                  style={{ cursor: "pointer" }}
                                  className="avtar avtar-xs btn-link-secondary editIcon"
                                >
                                  <i className="ti ti-edit f-20" />
                                </div> */}
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => openSinglePayment(row)}
                                      disabled={isNonPayable(row)}
                                    >
                                      {payButtonLabel(row)}
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      ) : (
                        <table className="table m-0" id="pc-dt-simple">
                          <thead>
                            <tr>
                              <th>Logo</th>
                              {/* <th>Logo</th> */}
                              <th>Company Name</th>
                              <th>Phone Number</th>
                              <th>Address</th>
                              <th>Website</th>
                              <th>Email</th>
                              <th>Created Date</th>
                              <th>Claim Requests</th>
                              <th>Pending Payment</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginated.length === 0 ? (
                              <tr>
                                <td colSpan="11" className="text-center text-muted">No data found</td>
                              </tr>
                            ) : (
                              paginated.map((row, index) => {
                                return (
                                  <>
                                    <tr key={row.id}>
                                      {
                                        row.logo ? <td
                                          style={{ cursor: "pointer" }}
                                          onClick={() => navigate(`/Admin/ViewCompany/${row.id}`, { state: { company: row } })}
                                          title="View company"
                                        >
                                          <img
                                            src={`${baseurImage}${row.logo}`}
                                            alt="Company Logo"
                                            style={{ width: '50px', height: '50px', objectFit: "cover", borderRadius: '4px' }}
                                          />
                                        </td> : <td
                                          style={{ cursor: "pointer" }}
                                          onClick={() => navigate(`/Admin/ViewCompany/${row.id}`, { state: { company: row } })}
                                          title="View company"
                                        ><i
                                          className="ti ti-building f-20 ms-4"
                                        ></i></td>
                                      }

                                      {/* <td>
  <div className="avtar avtar-s bg-light-secondary">
    {row.company_logo ? (
      <img
        src={`${baseurImage}${row.company_logo}`}
        alt="Company Logo"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "block";
        }}
      />
    ) : null}

    {/* fallback icon */}
                                      {/* <i
      className="ti ti-building f-20"
      style={{ display: row.company_logo ? "none" : "block" }}
    ></i>
  </div>
</td> */}                               <td
                                        style={{ cursor: "pointer", color: "#3f75c7", fontWeight: 500 }}
                                        onClick={() => navigate(`/Admin/ViewCompany/${row.id}`, { state: { company: row } })}
                                        title="View company"
                                      >{row.companyName || "-"}</td>
                                      <td>{row.telephone || "-"}</td>
                                      <td>{row.address || "-"}</td>
                                      <td>
                                        {row.website ? (
                                          <a href={row.website.startsWith('http') ? row.website : `https://${row.website}`} target="_blank" rel="noopener noreferrer">
                                            {row.website}
                                          </a>
                                        ) : "-"}
                                      </td>
                                      <td>{row.email || "-"}</td>
                                      <td>{row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB") : "-"}</td>
                                      <td>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary p-1 small"
                                          onClick={() => navigate(`/Admin/CompanyClaims/${row.id}`, { state: { company: row } })}
                                          title="View company claim requests"
                                        >
                                          {claimCountLoading && (claimCounts[row.id] == null) ? '…' : (claimCounts[row.id] ?? 0)}
                                        </button>
                                      </td>
                                      <td>
                                        {pendingAmountLoading && (pendingAmounts[row.id] == null) ? '…' : (
                                          (pendingAmounts[row.id] ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        )}
                                      </td>
                                      <td>
                                        <select
                                          className="form-select form-select-sm border-primary shadow-sm px-1 py-0"
                                          value={(row.status || 'active').toLowerCase()}
                                          onChange={async (e) => {
                                            const newStatus = e.target.value;
                                            await updateInsuranceCompanyStatus(row.id, newStatus);
                                          }}
                                          style={{ minWidth: '120px' }}
                                        >
                                          <option value="active">Active</option>
                                          <option value="inactive">Inactive</option>
                                        </select>
                                      </td>
                                      <td className="d-flex align-items-center gap-2">
                                        <div
                                          onClick={() => navigate(`/Admin/ViewCompany/${row.id}`, { state: { company: row } })}
                                          style={{ cursor: "pointer" }}
                                          className="avtar avtar-xs btn-link-secondary viewIcon"
                                        >
                                          <i className="ti ti-eye f-20" />
                                        </div>
                                        <div
                                          // onClick={() => navigate(`/Admin/EditCompany`, { state: { company: row } })}
                                          style={{ cursor: "pointer" }}
                                          className="avtar avtar-xs btn-link-secondary editIcon"
                                        >
                                          <i className="ti ti-edit f-20" />
                                        </div>
                                        <div
                                          style={{ cursor: "pointer" }}
                                          className="avtar avtar-xs btn-link-secondary deleteIcon"
                                          onClick={() => handleDeleteCompany(row.id)}
                                        >
                                          <i className="ti ti-trash f-20" />
                                        </div>
                                      </td>
                                    </tr>
                                  </>
                                )

                              })
                            )}
                          </tbody>
                        </table>
                      )}
                      {totalPages > 1 && (<div className="d-flex justify-content-end m-3">
                        <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                      </div>)}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div >

      {/* Create Company Modal */}
      {
        showCreateModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add New Insurance Company</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCreateModal(false)}
                  ></button>
                </div>
                <form onSubmit={handleCreateCompany}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Company Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="company_name"
                          value={newCompany.company_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Phone Number *</label>
                        <input
                          type="tel"
                          className="form-control"
                          name="phone_number"
                          value={newCompany.phone_number}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={newCompany.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Website</label>
                        <input
                          type="url"
                          className="form-control"
                          name="website"
                          value={newCompany.website}
                          onChange={handleInputChange}
                          placeholder="example.com"
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <label className="form-label">Address *</label>
                        <textarea
                          className="form-control"
                          name="address"
                          value={newCompany.address}
                          onChange={handleInputChange}
                          rows="3"
                          required
                        ></textarea>
                      </div>
                      <div className="col-12 mb-3">
                        <label className="form-label">Company Logo</label>
                        <input
                          type="file"
                          className="form-control"
                          name="company_logo"
                          accept="image/*"
                          onChange={handleInputChange}
                        />
                        <small className="form-text text-muted">Upload a logo for the company (optional)</small>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Create Company'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Payment - Step 1: Enter Payment Details */}
      {
        showPaymentStep1 && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">New Payment - Details</h5>
                  <button type="button" className="btn-close" onClick={() => setShowPaymentStep1(false)} />
                </div>
                <form onSubmit={proceedToSelectClaims}>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Company Name</label>
                        <select
                          className="form-select"
                          name="company_id"
                          value={paymentForm.company_id}
                          onChange={handlePaymentInputChange}
                          required
                        >
                          <option value="">Select company</option>
                          {companies.map((c) => (
                            <option key={c.id} value={c.id}>{c.companyName}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Date of Payment</label>
                        <input
                          type="date"
                          className="form-control"
                          name="payment_date"
                          value={paymentForm.payment_date}
                          onChange={handlePaymentInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Total Amount (BHD)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="total_amount"
                          value={paymentForm.total_amount}
                          onChange={handlePaymentInputChange}
                          placeholder="e.g. 120.00"
                        />
                        <small className="text-muted">Will be validated against selected claims in next step.</small>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Payment ID / Reference</label>
                        <input
                          type="text"
                          className="form-control"
                          name="payment_ref"
                          value={paymentForm.payment_ref}
                          onChange={handlePaymentInputChange}
                          placeholder="Bank transaction ID, etc"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Attachment (Proof of Payment)</label>
                        <input
                          type="file"
                          className="form-control"
                          name="attachment"
                          onChange={handlePaymentInputChange}
                          accept="image/*,application/pdf"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentStep1(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={paymentLoading}>
                      {paymentLoading ? 'Loading...' : 'Next'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Payment - Step 2: Select Accepted Claims */}
      {
        showPaymentStep2 && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Select Accepted Claims</h5>
                  <button type="button" className="btn-close" onClick={() => { setShowPaymentStep2(false); setShowPaymentStep1(true); }} />
                </div>
                <div className="modal-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <strong>Company:</strong> {companies.find(c => String(c.id) === String(paymentForm.company_id))?.companyName || '-'}
                    </div>
                    <div className="text-end">
                      <div><strong>Entered Amount:</strong> {paymentForm.total_amount ? Number(paymentForm.total_amount).toFixed(2) : '0.00'} BHD</div>
                      <div><strong>Selected Total:</strong> {Number(selectedTotal).toFixed(2)} BHD</div>
                    </div>
                  </div>

                  {acceptedLoading ? (
                    <div className="text-center my-3">Loading accepted claims...</div>
                  ) : (
                    <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Claim ID</th>
                            <th>Date</th>
                            <th>Patient</th>
                            <th>Policy #</th>
                            <th className="text-end">Amount (BHD)</th>
                            <th className="text-end">Manual Amount (BHD)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(!acceptedClaims || acceptedClaims.length === 0) ? (
                            <tr><td colSpan="7" className="text-center text-muted">No accepted claims found for this company</td></tr>
                          ) : (
                            acceptedClaims.map((c) => (
                              <tr key={c.claim_id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedClaimIds.has(c.claim_id)}
                                    onChange={() => toggleSelectClaim(c)}
                                  />
                                </td>
                                <td>{c.claim_number || c.claim_id}</td>
                                <td>{c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '-'}</td>
                                <td>{c.patient_name || '-'}</td>
                                <td>{c.policy_number || '-'}</td>
                                <td className="text-end">{Number(c.insurance_amount || c.amount || 0).toFixed(2)}</td>
                                <td className="text-end">
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ width: '100px', textAlign: 'right' }}
                                    value={manualAmounts[c.claim_id] || ''}
                                    onChange={(e) => handleManualAmountChange(c.claim_id, e.target.value)}
                                    placeholder={Number(c.insurance_amount || c.amount || 0).toFixed(2)}
                                    min="0"
                                    step="0.01"
                                  />
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer d-flex justify-content-between">
                  <div className="text-muted">
                    {paymentForm.total_amount && Number(paymentForm.total_amount) !== Number(selectedTotal) ? (
                      <span className="text-danger">Entered total and selected total do not match.</span>
                    ) : (
                      <span>Totals match or no total entered.</span>
                    )}
                  </div>
                  <div>
                    <button type="button" className="btn btn-secondary me-2" onClick={() => { setShowPaymentStep2(false); setShowPaymentStep1(true); }}>Back</button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={paymentLoading || selectedClaimIds.size === 0}
                      onClick={async () => {
                        if (paymentForm.total_amount && Number(paymentForm.total_amount) !== Number(selectedTotal)) {
                          const confirm = await Swal.fire({ title: 'Totals do not match', text: 'Proceed anyway?', icon: 'warning', showCancelButton: true });
                          if (!confirm.isConfirmed) return;
                        }
                        await submitPayment();
                      }}
                    >
                      {paymentLoading ? 'Paying...' : 'Pay'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Single Claim Payment Modal */}
      {
        showSinglePaymentModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Pay Individual Claim</h5>
                  <button type="button" className="btn-close" onClick={() => setShowSinglePaymentModal(false)} />
                </div>
                <form onSubmit={(e) => { e.preventDefault(); submitSinglePayment(); }}>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Date of Payment *</label>
                        <input
                          type="date"
                          className="form-control"
                          name="payment_date"
                          value={singlePaymentForm.payment_date}
                          onChange={handleSinglePaymentInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Amount (BHD) *</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="total_amount"
                          value={singlePaymentForm.total_amount}
                          onChange={handleSinglePaymentInputChange}
                          placeholder="e.g. 120.00"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Payment ID / Reference</label>
                        <input
                          type="text"
                          className="form-control"
                          name="payment_ref"
                          value={singlePaymentForm.payment_ref}
                          onChange={handleSinglePaymentInputChange}
                          placeholder="Bank transaction ID, etc"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Attachment (Proof of Payment)</label>
                        <input
                          type="file"
                          className="form-control"
                          name="attachment"
                          onChange={handleSinglePaymentInputChange}
                          accept="image/*,application/pdf"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowSinglePaymentModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={singlePaymentLoading}
                    >
                      {singlePaymentLoading ? 'Processing...' : 'Pay Claim'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}

function Pagination({ currentPage, totalPages, setCurrentPage }) {
  return (
    <div className="d-flex align-items-center gap-3">
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
      >
        &lt;&lt;
      </button>
      <span>{currentPage} / {totalPages}</span>
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        &gt;&gt;
      </button>
    </div>
  );
}

